import { Box, InputAdornment, TextField } from '@mui/material';
import { PageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import { useRef, useState } from 'react';
import { AgButton, AgText } from 'src/components/ui';

type PageSelectProps = { pageNavigationPlugin: PageNavigationPlugin };

export function PageSelect({ pageNavigationPlugin }: PageSelectProps) {
  const { jumpToPage, jumpToPreviousPage, jumpToNextPage, CurrentPageLabel } = pageNavigationPlugin;
  const [currentPageInput, setCurrentPageInput] = useState('0');
  const [editCurrentPage, setEditCurrentPage] = useState(false);
  const pageInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(1);

  return (
    <>
      <CurrentPageLabel>
        {({ currentPage, numberOfPages }) => {
          // Current Page Label flashes when zoom changes,
          // So we'll collect the state to prevent that.
          setNumberOfPages(numberOfPages);
          setCurrentPage(currentPage);

          return null;
        }}
      </CurrentPageLabel>
      <Box sx={{ display: 'flex', width: '95px', justifyContent: 'space-between' }}>
        {!editCurrentPage && (
          <AgButton
            variant="ghost"
            size="icon-xs"
            shape="circle"
            onPress={jumpToPreviousPage}
            isDisabled={currentPage === 0}
          >
            <RiArrowLeftSLine size={14} />
          </AgButton>
        )}
        {editCurrentPage ? (
          <TextField
            onChange={(e) => {
              setCurrentPageInput(e.target.value?.length ? Number(e.target.value).toString() : '');
            }}
            onKeyDown={(e) => {
              const value = Number(currentPageInput);

              if (e.key === 'Enter' && value) {
                jumpToPage(value - 1);
              }
            }}
            onBlur={() => setEditCurrentPage(false)}
            value={currentPageInput}
            size="small"
            sx={{ width: '70px', mx: 'auto' }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">/ {numberOfPages}</InputAdornment>,
                ref: pageInputRef,
                style: { fontSize: '14px' },
              },
            }}
          />
        ) : (
          <AgText
            size="14"
            color="secondary"
            style={{ userSelect: 'none', cursor: numberOfPages === 1 ? 'auto' : 'pointer' }}
            onClick={() => {
              if (numberOfPages === 1) {
                return;
              }

              setEditCurrentPage(true);
              setCurrentPageInput(currentPage + 1);

              setTimeout(() => {
                const inputElement = pageInputRef.current?.querySelector('input');
                inputElement?.focus();
              });
            }}
          >
            {currentPage + 1} / {numberOfPages}
          </AgText>
        )}
        {!editCurrentPage && (
          <AgButton
            variant="ghost"
            size="icon-xs"
            shape="circle"
            onPress={jumpToNextPage}
            isDisabled={currentPage === numberOfPages - 1}
          >
            <RiArrowRightSLine size={14} />
          </AgButton>
        )}
      </Box>
    </>
  );
}
